<script setup>
import { useI18n } from 'vue-i18n'
import ProvisionalMark from '@/components/ProvisionalMark.vue'
import { podiumClass } from '@/lib/podium'
import { deltaColor, deltaIcon, formatDelta, formatRating } from '@/lib/rating'

/**
 * Where a player stands: the rating and the place it is worth in the overall ranking.
 *
 * The row is the one the standings table receives (see GET /api/players/{username}), so the numbers
 * are read exactly as they are there. A player who never raced has no row at all: the card says so
 * instead of showing a line of zeros.
 */
defineProps({
  /** Ranking row of the player, or null when they have no place in the standings yet. */
  ranking: { type: Object, default: null },
  /** Window of the recent activity (`{ days }`), from the same payload. */
  period: { type: Object, default: null },
})

const { t } = useI18n()

/** The profile reads a drop in amber: the warning orange belongs to the standings. */
const PLAYER_DOWN_COLOR = 'amber-darken-2'
const playerDeltaColor = (value) => deltaColor(value, { down: PLAYER_DOWN_COLOR })
const deltaClass = (value) => `text-${playerDeltaColor(value)}`
</script>

<template>
  <v-card elevation="3" rounded="lg" class="overflow-hidden">
    <!-- <div class="d-flex align-center ga-3 pa-4">
      <v-avatar color="primary" variant="tonal" size="48">
        <v-icon size="28">mdi-chart-line</v-icon>
      </v-avatar>
      <div class="flex-grow-1">
        <h2 class="text-h6 font-weight-bold">{{ t('players.ranking') }}</h2>
        <p class="text-caption text-medium-emphasis mb-0">{{ t('players.rankingSubtitle') }}</p>
      </div>
    </div>

    <v-divider /> -->

    <div v-if="ranking" class="stats">
      <div class="stat stat--lead">
        <span class="stat__value">
          {{ formatRating(ranking.rating) }}<ProvisionalMark v-if="ranking.provisional" />
        </span>
        <span class="stat__label">{{ t('standings.rating') }}</span>
      </div>

      <div class="stat stat--lead">
        <span class="stat__value" :class="podiumClass(ranking.position)">
          {{ ranking.position }}
        </span>
        <span class="stat__label">{{ t('players.position') }}</span>
      </div>

      <div v-if="period?.days" class="stat">
        <span
          class="stat__value d-flex align-center justify-center ga-1"
          :class="deltaClass(ranking.periodDelta)"
        >
          <v-icon
            v-if="ranking.periodDelta !== null"
            :icon="deltaIcon(ranking.periodDelta)"
            :color="playerDeltaColor(ranking.periodDelta)"
            size="18"
          />
          {{ formatDelta(ranking.periodDelta) }}
        </span>
        <span class="stat__label">{{ t('standings.recentDelta', { days: period.days }) }}</span>
      </div>

      <div v-if="period?.days" class="stat">
        <span class="stat__value">{{ ranking.periodRaces || '–' }}</span>
        <span class="stat__label">{{ t('standings.recentRacesLabel', { days: period.days }) }}</span>
      </div>

      <!--
      <div class="stat">
        <span class="stat__value">{{ ranking.played }}</span>
        <span class="stat__label">{{ t('standings.played') }}</span>
      </div>

      <div class="stat">
        <span class="stat__value">{{ ranking.wins }}</span>
        <span class="stat__label">{{ t('standings.wins') }}</span>
      </div>

      <div class="stat">
        <span class="stat__value">{{ ranking.podium }}</span>
        <span class="stat__label">{{ t('standings.podium') }}</span>
      </div>
      -->
    </div>

    <div v-else class="d-flex align-center ga-2 pa-4 text-medium-emphasis">
      <v-icon size="20">mdi-chart-line-variant</v-icon>
      <span class="text-caption">{{ t('players.notRanked') }}</span>
    </div>
  </v-card>
</template>

<style scoped>
/* The numbers sit side by side and wrap onto another line when the screen is narrow, so the block
   stays readable on a phone without a media query. */
.stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px;
}

.stat {
  flex: 1 1 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 8px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 10px;
}

/* The two numbers that make the standing — the rating and the position — carry the block. */
.stat--lead {
  flex-grow: 2;
}

.stat__value {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.1;
}

.stat__label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.7;
}
</style>
